import { useState, useEffect } from "react";
import axios from "axios";
import { Plus, Minus, Check, ShoppingCart } from "lucide-react";
import { useCart } from "@/App";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import RelatedProducts from "./RelatedProducts";

const ADDED_FEEDBACK_DURATION = 1500;

/**
 * Quick-view product modal that shows full product + complementary picks.
 *
 * Props:
 *  - product: the focused product object
 *  - open: boolean
 *  - onOpenChange: (bool) => void
 */
export default function ProductQuickView({ product, open, onOpenChange }) {
  const { addToCart, cart } = useCart();
  const [selectedWeight, setSelectedWeight] = useState(product?.weight_options?.[0] || null);
  const [quantity, setQuantity] = useState(1);
  const [justAdded, setJustAdded] = useState(false);
  const [focused, setFocused] = useState(product);
  const [variants, setVariants] = useState([]);

  // When dialog re-opens for a new product, reset state
  useEffect(() => {
    setFocused(product);
    setSelectedWeight(product?.weight_options?.[0] || null);
    setQuantity(1);
  }, [product]);

  // Fetch size variants for the focused product (Damatol 55g/110g/250g, Eco Styler trio, etc.)
  useEffect(() => {
    if (!focused?.id) {
      setVariants([]);
      return;
    }
    const API = process.env.REACT_APP_BACKEND_URL;
    let cancelled = false;
    axios
      .get(`${API}/api/products/${focused.id}/variants`)
      .then((res) => {
        if (!cancelled) setVariants(res.data || []);
      })
      .catch(() => {
        if (!cancelled) setVariants([]);
      });
    return () => {
      cancelled = true;
    };
  }, [focused?.id]);

  if (!focused) return null;

  const displayPrice =
    focused.is_special && focused.special_price ? focused.special_price : focused.price;
  const inCart = cart.find(
    (item) => item.product_id === focused.id && item.weight_option === selectedWeight
  );

  const handleAddToCart = () => {
    addToCart(focused, quantity, selectedWeight);
    setJustAdded(true);
    toast.success(`${focused.name} added to cart!`);
    setTimeout(() => setJustAdded(false), ADDED_FEEDBACK_DURATION);
    setQuantity(1);
  };

  // When user picks a complementary item, swap the quick-view focus to that product
  const handleRelatedClick = (p) => {
    setFocused(p);
    setSelectedWeight(p.weight_options?.[0] || null);
    setQuantity(1);
    setJustAdded(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-6xl max-h-[90vh] p-0 overflow-hidden"
        data-testid="quick-view-dialog"
      >
        <DialogTitle className="sr-only">{focused.name}</DialogTitle>
        <ScrollArea className="max-h-[90vh]">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-0">
            {/* Image */}
            <div className="relative aspect-square bg-slate-100">
              {focused.image_url ? (
                <img
                  src={focused.image_url}
                  alt={focused.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-slate-400">
                  No image
                </div>
              )}
              {focused.is_special && (
                <Badge className="absolute top-3 left-3 bg-red-600 text-white">SPECIAL OFFER</Badge>
              )}
              {focused.stock === 0 && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <Badge variant="destructive" className="text-base">
                    Out of Stock
                  </Badge>
                </div>
              )}
            </div>

            {/* Details */}
            <div className="p-6 flex flex-col">
              <h2 className="text-2xl font-bold text-slate-900 mb-2" data-testid="quick-view-name">
                {focused.name}
              </h2>
              {focused.description && (
                <p className="text-sm text-slate-600 mb-4">{focused.description}</p>
              )}

              <div className="flex items-baseline gap-2 mb-4">
                <span className="text-3xl font-bold text-emerald-600">
                  £{displayPrice.toFixed(2)}
                </span>
                <span className="text-sm text-slate-500">/ {focused.unit}</span>
                {focused.is_special && focused.special_price && (
                  <span className="text-base text-slate-400 line-through">
                    £{focused.price.toFixed(2)}
                  </span>
                )}
              </div>

              {variants.length > 0 && (
                <div className="mb-4" data-testid="quick-view-variants">
                  <label className="text-xs font-medium text-slate-700 mb-2 block">
                    Available sizes
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {/* Current focused size pill */}
                    <button
                      type="button"
                      className="px-3 py-1.5 rounded-full text-sm font-medium border border-emerald-500 bg-emerald-50 text-emerald-700"
                      data-testid="quick-view-variant-current"
                    >
                      {(focused.name.match(/\d+(?:[.,]\d+)?\s*(?:kg|g|ml|l|oz|fl\s*oz)/i) || ["this size"])[0]}
                      <span className="ml-1.5 text-emerald-600/70">£{displayPrice.toFixed(2)}</span>
                    </button>
                    {variants.map((v) => {
                      const sizeMatch = v.name.match(/\d+(?:[.,]\d+)?\s*(?:kg|g|ml|l|oz|fl\s*oz)/i);
                      const label = sizeMatch ? sizeMatch[0] : v.name;
                      return (
                        <button
                          key={v.id}
                          type="button"
                          onClick={() => handleRelatedClick(v)}
                          className="px-3 py-1.5 rounded-full text-sm font-medium border border-slate-200 text-slate-700 hover:border-emerald-500 hover:bg-emerald-50 hover:text-emerald-700 transition-colors"
                          data-testid={`quick-view-variant-${v.id}`}
                        >
                          {label}
                          <span className="ml-1.5 text-slate-400">£{(v.is_special && v.special_price ? v.special_price : v.price).toFixed(2)}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {focused.weight_options && focused.weight_options.length > 0 && (
                <div className="mb-4">
                  <label className="text-xs font-medium text-slate-700 mb-1 block">
                    Weight option
                  </label>
                  <Select value={selectedWeight} onValueChange={setSelectedWeight}>
                    <SelectTrigger className="w-full" data-testid="quick-view-weight-select">
                      <SelectValue placeholder="Select weight" />
                    </SelectTrigger>
                    <SelectContent>
                      {focused.weight_options.map((w) => (
                        <SelectItem key={w} value={w}>
                          {w}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center border border-slate-200 rounded-lg">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-10 h-10 flex items-center justify-center text-slate-600 hover:text-emerald-600"
                    data-testid="quick-view-qty-decrease"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <span className="w-10 text-center font-semibold">{quantity}</span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="w-10 h-10 flex items-center justify-center text-slate-600 hover:text-emerald-600"
                    data-testid="quick-view-qty-increase"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>

                <Button
                  onClick={handleAddToCart}
                  disabled={focused.stock === 0}
                  className="flex-1 h-10 bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="quick-view-add-to-cart"
                >
                  {justAdded ? (
                    <>
                      <Check className="h-4 w-4 mr-2" /> Added
                    </>
                  ) : (
                    <>
                      <ShoppingCart className="h-4 w-4 mr-2" /> Add to Cart
                    </>
                  )}
                </Button>
              </div>

              {inCart && !justAdded && (
                <p className="text-sm text-emerald-600 mb-2">{inCart.quantity} in cart</p>
              )}

              {focused.stock < 10 && focused.stock > 0 && (
                <p className="text-xs text-amber-600">Only {focused.stock} left in stock</p>
              )}
            </div>
          </div>

          {/* Related products */}
          <div className="px-6 pb-6">
            <RelatedProducts
              productId={focused.id}
              title="You may also like"
              onItemClick={handleRelatedClick}
            />
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
